/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import BaseLayout from "layouts/pages/tugas/components/BaseLayout";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
// Material Dashboard 3 PRO React examples

import Footer from "examples/Footer";

// NewProduct page components
import Instruksi from "layouts/pages/tugas/tugas-baru/components/Instruksi";
import Media from "layouts/pages/tugas/tugas-baru/components/Media";
import Penerima from "layouts/pages/tugas/tugas-baru/components/Penerima";

import { API_ESA_OFFICE } from "utils";
import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";

// Validasi tipe file yang diizinkan
const isValidFileType = (file) => {
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ];
  const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".xlsm",
    ".jpg",
    ".jpeg",
    ".png",
  ];
  const fileExtension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."));
  return (
    allowedTypes.includes(file.type) ||
    allowedExtensions.includes(fileExtension)
  );
};

function getSteps() {
  return ["Instruksi", "Penerima Instruksi", "Dokumen"];
}

function getStepContent(stepIndex) {
  switch (stepIndex) {
    case 0:
      return <Instruksi />;
    case 1:
      return <Penerima />;
    case 2:
      return <Media />;

    default:
      return null;
  }
}

// Fungsi untuk memvalidasi semua file (tipe dan ukuran)
const validateAllFiles = (files) => {
  if (!files || !Array.isArray(files) || files.length === 0) {
    return { isValid: true, errors: [] };
  }

  const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB dalam bytes
  const errors = [];
  let totalSize = 0;

  files.forEach((fileItem, index) => {
    const fileObj =
      fileItem && typeof fileItem === "object"
        ? fileItem.file || fileItem
        : fileItem;

    // Validasi bahwa fileObj adalah object
    if (!fileObj || typeof fileObj !== "object") {
      errors.push({
        name: `File ${index + 1}`,
        reason: "File tidak valid (bukan object file)",
      });
      return;
    }

    // Validasi bahwa file memiliki nama
    if (!fileObj.name || typeof fileObj.name !== "string") {
      errors.push({
        name: `File ${index + 1}`,
        reason: "File tidak memiliki nama yang valid",
      });
      return;
    }

    // Validasi ukuran file
    const fileSize = fileObj.size;
    if (!fileSize || typeof fileSize !== "number" || fileSize <= 0) {
      errors.push({
        name: fileObj.name,
        reason: "File tidak memiliki ukuran yang valid",
      });
      return;
    }

    // Validasi tipe file
    if (!isValidFileType(fileObj)) {
      errors.push({
        name: fileObj.name,
        reason: "Format file tidak didukung",
      });
      return;
    }

    // Cek apakah penambahan file ini akan melebihi batas ukuran total
    const newTotalSize = totalSize + fileSize;
    if (newTotalSize > maxTotalSize) {
      const totalSizeMB = newTotalSize / (1024 * 1024);
      errors.push({
        name: fileObj.name,
        reason: `Total ukuran akan melebihi 1.5 MB (${totalSizeMB.toFixed(
          2
        )} MB)`,
      });
      return;
    }

    // File valid - tambahkan ke total ukuran
    totalSize += fileSize;
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
    totalSize: totalSize,
  };
};

function TugasBaru() {
  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();
  const isLastStep = activeStep === steps.length - 1;
  const navigate = useNavigate();
  const [dinasId, setDinasId] = useState(null);
  const [statusUser, setStatusUser] = useState(null);
  const [isSendDisabled, setIsSendDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State gabungan untuk semua step - HARUS dideklarasikan sebelum useEffect yang menggunakannya
  const [formData, setFormData] = useState({
    instruksi: {
      perihal: "",
      tanggal: "",
      tanggal_selesai: "",
      isi_instruksi: "",
      prioritas: null,
      status: "Terkirim",
      penerima: [],
      dokumen: null,
    },
    // Tambahkan state untuk step lain jika perlu
    // socials: {}, pricing: {}, dst.
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setStatusUser(user.status);
    } else {
      logoutUser(navigate);
    }
  }, []);

  // Handler untuk menerima status validitas file dari Media component
  // Menggunakan useCallback untuk memastikan function stabil dan tidak menyebabkan re-render
  const handleMediaValidationChange = useCallback((isInvalid) => {
    setIsSendDisabled(isInvalid);
  }, []);

  // Validasi file secara real-time untuk menonaktifkan tombol send (backup validasi)
  useEffect(() => {
    if (formData.instruksi.dokumen && formData.instruksi.dokumen.length > 0) {
      const validation = validateAllFiles(formData.instruksi.dokumen);
      // Nonaktifkan tombol send jika ada file yang tidak valid (tipe tidak didukung atau ukuran melebihi batas)
      setIsSendDisabled(!validation.isValid);

      // Log warning jika ada file yang tidak valid (untuk debugging)
      if (!validation.isValid) {
        console.warn("File tidak valid:", validation.errors);
      }
    } else {
      // Aktifkan tombol send jika tidak ada file
      setIsSendDisabled(false);
    }
  }, [formData.instruksi.dokumen]);

  // Handler untuk update data tiap step
  const updateStepData = (step, data) => {
    setFormData((prev) => ({ ...prev, [step]: { ...prev[step], ...data } }));
  };

  // Handler submit/send
  const handleSubmit = async () => {
    try {
      // Validasi field wajib sebelum submit
      const errors = [];

      if (
        !formData.instruksi.perihal ||
        formData.instruksi.perihal.trim() === ""
      ) {
        errors.push("Perihal");
      }

      if (
        !formData.instruksi.isi_instruksi ||
        formData.instruksi.isi_instruksi.trim() === ""
      ) {
        errors.push("Isi Instruksi");
      }

      if (
        !formData.instruksi.tanggal ||
        formData.instruksi.tanggal.trim() === ""
      ) {
        errors.push("Start (Tanggal)");
      }

      if (
        !formData.instruksi.tanggal_selesai ||
        formData.instruksi.tanggal_selesai.trim() === ""
      ) {
        errors.push("Finish (Tanggal Selesai)");
      }

      if (
        !formData.instruksi.prioritas ||
        formData.instruksi.prioritas === null
      ) {
        errors.push("Prioritas");
      }

      if (
        !formData.instruksi.penerima ||
        formData.instruksi.penerima.length === 0
      ) {
        errors.push("Penerima");
      }

      if (errors.length > 0) {
        swal({
          title: "Data Belum Lengkap",
          text: `Field berikut wajib diisi:\n\n${errors
            .map((e, i) => `${i + 1}. ${e}`)
            .join("\n")}\n\nSilakan lengkapi data terlebih dahulu.`,
          icon: "error",
          button: "OK",
        });
        return;
      }

      // Validasi file sebelum submit (tipe dan ukuran) - HARUS DILAKUKAN
      // Validasi ini sebagai backup, karena tombol send sudah dinonaktifkan jika file tidak valid
      if (formData.instruksi.dokumen && formData.instruksi.dokumen.length > 0) {
        const validation = validateAllFiles(formData.instruksi.dokumen);

        // Jika validasi gagal, TOLAK SUBMIT
        if (!validation.isValid) {
          const errorMessages = validation.errors
            .map((f, idx) => `${idx + 1}. ${f.name}: ${f.reason}`)
            .join("\n");
          swal({
            title: "File Tidak Valid",
            text: `File berikut tidak dapat dikirim:\n\n${errorMessages}\n\nFormat yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG, PNG\nMaksimal total ukuran: 1.5 MB\n\nSilakan hapus file yang tidak valid atau ganti dengan file yang sesuai.`,
            icon: "error",
            button: "OK",
          });
          return; // TOLAK SUBMIT
        }

        // Double check: Pastikan total ukuran tidak melebihi batas
        const totalSizeMB = validation.totalSize / (1024 * 1024);
        const maxSizeMB = 1.5;
        const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB dalam bytes

        if (validation.totalSize > maxTotalSize || totalSizeMB > maxSizeMB) {
          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal ${maxSizeMB} MB.\n\nSilakan hapus beberapa file atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
          return; // TOLAK SUBMIT
        }
      }

      // Ambil user dari localStorage
      const userStorage = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;
      const pengirim_id =
        userStorage && userStorage.uid ? userStorage.uid : null;
      // Format penerima
      const penerimaArr = (formData.instruksi.penerima || []).map((p) => ({
        penerima_id: p.value,
        asal_perintah_dari_id: pengirim_id,
        tingkat_perintah: 1,
        status_pelaksana: "Belum",
        instruksi_tindak_lanjut: "",
      }));
      // Format data sesuai permintaan
      const dataToSend = {
        pengirim_id,
        tanggal: formData.instruksi.tanggal,
        tanggal_selesai: formData.instruksi.tanggal_selesai,
        isi_instruksi: formData.instruksi.isi_instruksi,
        prioritas: formData.instruksi.prioritas,
        status: formData.instruksi.status,
        penerima: penerimaArr,
        dokumen: formData.instruksi.dokumen,
        perihal: formData.instruksi.perihal,
      };

      // Kirim dokumen sebagai FormData (multiple file)
      // Hanya kirim file yang sudah divalidasi dan valid
      if (formData.instruksi.dokumen && formData.instruksi.dokumen.length > 0) {
        // Final validation sebelum kirim - pastikan semua file valid
        const finalValidFiles = [];
        let finalTotalSize = 0;
        const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB dalam bytes

        formData.instruksi.dokumen.forEach((fileItem) => {
          const fileObj = fileItem.file || fileItem;

          // Validasi ulang sebelum kirim
          if (
            !fileObj ||
            typeof fileObj !== "object" ||
            !fileObj.name ||
            typeof fileObj.size !== "number" ||
            fileObj.size <= 0
          ) {
            console.warn(
              `File ${
                fileObj?.name || "Unknown"
              } tidak valid dan akan diabaikan`
            );
            return;
          }

          if (!isValidFileType(fileObj)) {
            console.warn(
              `File ${fileObj.name} format tidak didukung dan akan diabaikan`
            );
            return;
          }

          // Cek ukuran total
          if (finalTotalSize + fileObj.size > maxTotalSize) {
            console.warn(
              `File ${fileObj.name} akan menyebabkan total ukuran melebihi batas dan akan diabaikan`
            );
            return;
          }

          // File valid
          finalValidFiles.push(fileItem);
          finalTotalSize += fileObj.size;
        });

        // Jika tidak ada file valid sama sekali, tampilkan error
        if (
          finalValidFiles.length === 0 &&
          formData.instruksi.dokumen.length > 0
        ) {
          swal({
            title: "Tidak Ada File Valid",
            text: "Semua file yang dipilih tidak valid atau melebihi batas. Silakan pilih file yang sesuai.",
            icon: "error",
            button: "OK",
          });
          return;
        }

        setIsSubmitting(true);
        try {
          const formDataSend = new FormData();
          // Append field lain
          formDataSend.append("pengirim_id", pengirim_id);
          formDataSend.append("perihal", formData.instruksi.perihal);
          formDataSend.append("tanggal", formData.instruksi.tanggal);
          formDataSend.append(
            "tanggal_selesai",
            formData.instruksi.tanggal_selesai
          );
          formDataSend.append("isi_instruksi", formData.instruksi.isi_instruksi);
          formDataSend.append("prioritas", formData.instruksi.prioritas);
          formDataSend.append("status", formData.instruksi.status);
          formDataSend.append("penerima", JSON.stringify(penerimaArr));

          // Append dokumen satu per satu - HANYA file yang valid
          finalValidFiles.forEach((dzFile) => {
            const fileObj = dzFile.file || dzFile;
            // Pastikan file yang dikirim adalah File asli dan valid
            if (fileObj && fileObj instanceof File) {
              formDataSend.append("dokumen", fileObj);
            }
          });
          const res = await axios.post(
            `${API_ESA_OFFICE}instruksi`,
            formDataSend,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          const instruksiId = res.data?.instruksi_id;
          if (instruksiId) {
            swal("Sukses", "Instruksi berhasil dikirim!", "success").then(
              () => {
                navigate(
                  `/task-management/tugas/instruksi-detail/${instruksiId}`
                );
              }
            );
          } else {
            swal("Sukses", "Instruksi berhasil dikirim!", "success");
          }
        } finally {
          setIsSubmitting(false);
        }
      } else {
        // Jika tidak ada file, kirim data biasa
        setIsSubmitting(true);
        try {
          const res = await axios.post(`${API_ESA_OFFICE}instruksi`, dataToSend);
          const instruksiId = res.data?.instruksi_id;
          if (instruksiId) {
            swal("Sukses", "Instruksi berhasil dikirim!", "success").then(
              () => {
                navigate(
                  `/task-management/tugas/instruksi-detail/${instruksiId}`
                );
              }
            );
          } else {
            swal("Sukses", "Instruksi berhasil dikirim!", "success");
          }
        } finally {
          setIsSubmitting(false);
        }
      }
      // Reset formData dan step ke 0
      setFormData({
        instruksi: {
          perihal: "",
          tanggal: "",
          tanggal_selesai: "",
          isi_instruksi: "",
          prioritas: null,
          status: "Terkirim",
          penerima: [],
          dokumen: null,
        },
        // Tambahkan state untuk step lain jika perlu
        // socials: {}, pricing: {}, dst.
      });
      setActiveStep(0);
    } catch (err) {
      swal("Gagal", "Gagal mengirim instruksi", "error");
    }
  };

  // Step content dengan prop update
  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <Instruksi
            values={formData.instruksi}
            onChange={(data) => updateStepData("instruksi", data)}
          />
        );

      case 1:
        return (
          <Penerima
            values={formData.instruksi}
            onChange={(data) => updateStepData("instruksi", data)}
          />
        );
      case 2:
        return (
          <Media
            values={formData.instruksi}
            onChange={(data) => updateStepData("instruksi", data)}
            onValidationChange={handleMediaValidationChange}
          />
        );
      default:
        return null;
    }
  }

  const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox mt={2} mb={3} mx={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>
              <MDBox p={2}>
                <MDBox>
                  {getStepContent(activeStep)}
                  <MDBox
                    mt={3}
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                  >
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton
                        variant="gradient"
                        color="light"
                        onClick={handleBack}
                        disabled={isSubmitting}
                      >
                        back
                      </MDButton>
                    )}
                    <MDButton
                      variant="gradient"
                      color="dark"
                      onClick={!isLastStep ? handleNext : handleSubmit}
                      disabled={
                        isLastStep && (isSendDisabled || isSubmitting)
                      }
                    >
                      {isLastStep ? (
                        isSubmitting ? (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1}
                            color="inherit"
                          >
                            <CircularProgress size={18} color="inherit" />
                            <MDTypography
                              variant="button"
                              color="inherit"
                              fontWeight="medium"
                            >
                              Mengirim…
                            </MDTypography>
                          </MDBox>
                        ) : (
                          "send"
                        )
                      ) : (
                        "next"
                      )}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default TugasBaru;
